<template>
  <div style="width: 100%; font-family: SF Pro Display, sans-serif">
    <el-row
      style="
        height: 60px;
        line-height: 60px;
        background: #f9f9f9;
        padding-left: 40px;
      "
    >
      {{ $t('plan_payment.org_name_plan_title', [organizationData.name]) }}
    </el-row>
    <div class="plan-payment">
      <h2 class="title">{{ $t('plan_payment.title') }}</h2>
      <p class="desc">
        {{ $t('plan_payment.desc') }}
      </p>
      <el-row :gutter="30">
        <el-col :xl="14" :lg="14" :md="24">
          <el-card shadow="never" style="margin-bottom: 30px">
            <payment-plan
              ref="paymentPlan"
              :products="products"
              :country="country"
              :benefits="benefits"
              @set-product="onSetProduct"
            />
          </el-card>
          <el-card shadow="never" style="margin-bottom: 30px">
            <payment-method
              ref="stripeForm"
              :product="product"
              @set-loading="setLoading"
            />
          </el-card>
        </el-col>
        <el-col :xl="10" :lg="10" :md="24">
          <el-card shadow="never">
            <payment-summary
              :product="selectedProduct"
              :quantity="product.quantity"
              :locale="locale"
              :loading="loading"
              @subscribe="onSubscribe"
            />
          </el-card>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
  import {INTERVAL, CODE_SITE } from '@/constants/generic';
  import PaymentSummary from '@/components/payment/PaymentSummary';
  import PaymentPlan from '@/components/payment/PaymentPlan';
  import PaymentMethod from '@/components/payment/PaymentMethod';

  export default {
    components: { PaymentMethod, PaymentSummary, PaymentPlan },
    data () {
      return {
        product: {
          package: undefined,
          product: undefined,
          currency: 'USD',
          quantity: 5,
          price: 0,
          saving: 0
        },
        loading: false,
        products: [],
        selectedProduct: undefined,
        defaultCountry: 'US',
        country: 'US',
        countryCodeMapping: {
          'ES': 'ES',
          'JP': 'JA',
          'KP': 'KO',
          'KR': 'KO',
          'PT': 'PT',
          'BR': 'PT',
          'VN': 'VI'
        },
        countryLocaleMapping: {
          'ES': 'es-ES',
          'JP': 'ja-JP',
          'KP': 'ko',
          'KR': 'ko-KR',
          'PT': 'pt-PT',
          'BR': 'pt-BR',
          'VN': 'vi-VN'
        },
        locale: 'en-US',
        prefixUriSite: '/v1/site/',
        benefits: {},
      };
    },
    methods: {
      async initProducts () {
        try {
          // load country by ip
          const ip = await this.$axios.get('https://elsaspeak.com/api/get-ip');
          this.country = ip.data ? ip.data['country'] : this.defaultCountry;
          if (this.country === 'XX') {
            this.country = this.defaultCountry;
          }
        } catch (error) {
          this.country = this.defaultCountry;
        }
        // load discount code by country
        // this.codePrefix = (this.country in this.countryCodeMapping) ? this.countryCodeMapping[this.country] : '';
        
        this.locale = (this.country in this.countryLocaleMapping) ? this.countryLocaleMapping[this.country] : 'en-US';
        const lite_uri = this.prefixUriSite + CODE_SITE.LITE;
        const medium_uri = this.prefixUriSite + CODE_SITE.MEDIUM;
        // load products
        const params = {
          'country': this.country,
        }
        const lite_request = this.$axios.get(process.env.VUE_APP_PRODUCT_CATALOG_URL + lite_uri , {params}).then((response) => {
          const data_lite = response.data["catalogs"];
          data_lite.forEach(item => {
            const products = item["products"];
            const variants = item["variants"];
            let product_content_ids = [];
            let product_content = products[0]["content"];
            products.forEach(product => {
              product_content_ids = [...product_content_ids, ...product["content_ids"]];
            });
            variants.forEach(variant => {
              const pricePerMonth = this.getPricePerMonth(variant.price, variant.interval);
              const label = variant.interface.box_title;
              const catalog_code = item["code"];
              const ele = Object.assign({ pricePerMonth, label, catalog_code, product_content, product_content_ids}, variant);
              this.products.push(ele);
            });
          })
        }).catch((error) => {
          throw error
        });
        const medium_request = this.$axios.get(process.env.VUE_APP_PRODUCT_CATALOG_URL + medium_uri , {params}).then((response) => {
          const data_medium = response.data["catalogs"];
          data_medium.forEach(item => {
            const products = item["products"];
            const variants = item["variants"];
            let product_content_ids = [];
            let product_content = products[0]["content"];
            products.forEach(product => {
              product_content_ids = [...product_content_ids, ...product["content_ids"]];
            });
            variants.forEach(variant => {
              const pricePerMonth = this.getPricePerMonth(variant.price, variant.interval);
              const label = variant.interface.box_title;
              const catalog_code = item["code"];
              const ele = Object.assign({ pricePerMonth, label, catalog_code, product_content, product_content_ids}, variant);
              this.products.push(ele);
            });
            this.benefits[item["code"]] = item["benefits"];
          })
        }).catch((error) => {
          throw error
        });
        await Promise.all([lite_request, medium_request]);
        this.products.sort((a, b) => a.price > b.price ? -1 : 1).sort((a, b) => a.code < b.code ? -1 : 1);
        this.selectedProduct = this.products[0];
        // select default product for Plan Form
        this.$refs.paymentPlan.handleProductChange(this.selectedProduct.id)
      },
      getPricePerMonth(price, interval) {
        switch (interval) {
          case INTERVAL.ONE_YEAR.key:
            return price / INTERVAL.ONE_YEAR.value;
          case INTERVAL.SIX_MONTHS.key:
            return price / INTERVAL.SIX_MONTHS.value;
          case INTERVAL.THREE_MONTHS.key:
            return price / INTERVAL.THREE_MONTHS.value;
          default:
            return 0;
        }
      },
      async initOrganization () {
        const organizationId = this.organizationId;
        if (!this.organizationData) {
          await this.$store.dispatch('organization/getCurrentOrganization', {
            organizationId
          });
        }
      },
      onSetProduct (product) {
        this.product = product;
        this.selectedProduct = this.products.find(product => product.id === this.product.product);
      },
      onSubscribe () {
        this.loading = true
        this.$refs.stripeForm.submit();
      },
      setLoading(value) {
        this.loading = value
      }
    },
    computed: {
      organizationId () {
        return this.$store.state.organization.currentOrganizationId;
      },
      organizationData () {
        return this.$store.state.organization.currentOrganization;
      }
    },
    async mounted () {
      await Promise.all([this.initOrganization(), this.initProducts()]);
    }
  };
</script>

<style lang="scss" scoped>
  .plan-payment {
    padding: 32px 40px;

    .title {
      font-weight: 600;
      font-size: 32px;
      letter-spacing: unset;
    }

    .desc {
      font-size: 18px;
    }

    .el-card {
      border: 1px solid #E8E8E8;
      box-sizing: border-box;
      border-radius: 16px;
      background: #FFFFFF;
    }

    h1 {
      letter-spacing: unset !important;
    }
  }
</style>
